import React, { useMemo } from "react";
import {
  Badge,
  Box,
  Table as ChakraTable,
  Icon,
  Text,
} from "@chakra-ui/react";
import { FaAngleDown } from "react-icons/fa6";
import { IssueSeverity, IssueStatus, RuleDef, SummaryEntry } from "./types";

const STATUS_LABEL: Partial<
  Record<`${IssueStatus}:${IssueSeverity}`, [string, string]>
> = {
  "passed:error": ["Passed", "green"],
  "passed:warning5": ["Passed", "green"],
  "passed:warning1": ["Passed", "green"],
  "passed:observation": ["Passed", "green"],
  "failed:error": ["Failed", "red"],
  "failed:warning5": ["Needs Your Attention", "orange"],
  "failed:warning1": ["Conditionally Passed", "yellow"],
  "generated:observation": ["Report Generated", "gray"],
};

const renderStatus = (
  status: IssueStatus | undefined,
  severity: IssueSeverity | undefined
): [string, string] => {
  if (!status || !severity) return ["Not Checked", "gray"];
  return STATUS_LABEL[`${status}:${severity}`] ?? ["", "gray"];
};

type Row =
  | { kind: "category"; category: string }
  | {
      kind: "rule";
      rule: string;
      description: string;
      severity: IssueSeverity;
      status: IssueStatus | undefined;
      count: number | null | undefined;
    };

export const Table: React.FC<{
  summary: Record<string, SummaryEntry>;
  rules: RuleDef[];
  onRowClick: (ruleId: string) => void;
}> = ({ summary, rules, onRowClick }) => {
  const rows = useMemo<Row[]>(() => {
    const byCategory = new Map<string, RuleDef[]>();
    for (const r of rules) {
      const list = byCategory.get(r.category) ?? [];
      list.push(r);
      byCategory.set(r.category, list);
    }
    const out: Row[] = [];
    for (const [category, items] of byCategory.entries()) {
      out.push({ kind: "category", category });
      for (const r of items) {
        const entry = summary[r.id];
        out.push({
          kind: "rule",
          rule: r.id,
          description: r.summary,
          severity: r.severity,
          status: entry?.status,
          count: entry?.count,
        });
      }
    }
    return out;
  }, [summary, rules]);

  return (
    <ChakraTable.Root size="sm" striped stickyHeader interactive variant="outline">
      <ChakraTable.Header>
        <ChakraTable.Row bg="bg.subtle">
          <ChakraTable.ColumnHeader>
            <Text fontWeight="bold">Rule</Text>
          </ChakraTable.ColumnHeader>
          <ChakraTable.ColumnHeader>
            <Text fontWeight="bold">Description</Text>
          </ChakraTable.ColumnHeader>
          <ChakraTable.ColumnHeader>
            <Text fontWeight="bold">Status</Text>
          </ChakraTable.ColumnHeader>
        </ChakraTable.Row>
      </ChakraTable.Header>
      <ChakraTable.Body>
        {rows.map((row) => {
          if (row.kind === "category") {
            return (
              <ChakraTable.Row key={`category-${row.category}`}>
                <ChakraTable.Cell colSpan={3} textAlign="center">
                  <Text fontWeight="bold">{row.category}</Text>
                </ChakraTable.Cell>
              </ChakraTable.Row>
            );
          }
          const [label, color] = renderStatus(row.status, row.severity);
          const clickable =
            (row.status === "failed" || row.status === "generated") &&
            (row.count ?? 0) > 0;
          const countText =
            row.count != null && row.count > 0 ? ` (${row.count})` : "";
          return (
            <ChakraTable.Row
              key={`rule-${row.rule}`}
              onClick={clickable ? () => onRowClick(row.rule) : undefined}
              cursor={clickable ? "pointer" : undefined}
            >
              <ChakraTable.Cell>
                <Text fontWeight="bold">{row.rule}</Text>
              </ChakraTable.Cell>
              <ChakraTable.Cell>{row.description}</ChakraTable.Cell>
              <ChakraTable.Cell>
                {clickable ? (
                  <Box>
                    <Badge colorPalette={color}>
                      {label}
                      {countText}
                    </Badge>
                    <Icon fontSize="2xl" color="gray" size="sm">
                      <FaAngleDown />
                    </Icon>
                  </Box>
                ) : (
                  <Badge colorPalette={color}>{label}</Badge>
                )}
              </ChakraTable.Cell>
            </ChakraTable.Row>
          );
        })}
      </ChakraTable.Body>
    </ChakraTable.Root>
  );
};
